/**
 * The seven aggregate state machines (PROJECT_SPEC.md §5.3 statuses, §4.1 phase model, §5.5 versioning,
 * §6.2 material status, §6.5 exception review, §6.6 disposition). Tables are the design spec (§11.3 #4).
 * ADR-0003: `batch` has an extra terminal ABORTED state (WF-020 "batch abort/cancel … prevents further
 * execution") because §5.3's status list has no terminal state for an aborted batch.
 */
import { EQUIPMENT_STATUS } from "./equipment-state.js";
import type { Machine } from "./machine.js";

// ---------------------------------------------------------------- batch (§5.3 B + ABORTED)
export type BatchState =
  | "CREATED"
  | "RELEASED"
  | "IN_PROGRESS"
  | "ON_HOLD"
  | "EXECUTION_COMPLETE"
  | "IN_REVIEW"
  | "QA_APPROVED"
  | "REJECTED"
  | "CLOSED"
  | "ABORTED";
export type BatchEvent =
  | "ReleaseToProduction"
  | "Start"
  | "Hold"
  | "Resume"
  | "CompleteExecution"
  | "SubmitForReview"
  | "ReturnToProduction"
  | "Approve"
  | "Reject"
  | "Close"
  | "Abort";
export const BATCH: Machine<BatchState, BatchEvent> = {
  id: "batch",
  initial: "CREATED",
  states: [
    "CREATED",
    "RELEASED",
    "IN_PROGRESS",
    "ON_HOLD",
    "EXECUTION_COMPLETE",
    "IN_REVIEW",
    "QA_APPROVED",
    "REJECTED",
    "CLOSED",
    "ABORTED",
  ],
  events: [
    "ReleaseToProduction",
    "Start",
    "Hold",
    "Resume",
    "CompleteExecution",
    "SubmitForReview",
    "ReturnToProduction",
    "Approve",
    "Reject",
    "Close",
    "Abort",
  ],
  terminal: ["CLOSED", "ABORTED"],
  transitions: [
    {
      from: "CREATED",
      on: "ReleaseToProduction",
      to: "RELEASED",
      guards: ["mbrEffective", "batchNumberUnique"],
      permissions: ["batch.create"],
      cite: "WF-019, WF-021",
    },
    {
      from: "CREATED",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void", "batch.release"],
      cite: "WF-020",
    },
    {
      from: "RELEASED",
      on: "Start",
      to: "IN_PROGRESS",
      guards: ["equipmentReady", "trainingCurrent"],
      permissions: ["batch.step.perform"],
      cite: "WF-015, WF-016",
    },
    {
      from: "RELEASED",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void", "batch.release"],
      cite: "WF-020",
    },
    {
      from: "IN_PROGRESS",
      on: "Hold",
      to: "ON_HOLD",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform", "batch.step.void"],
      cite: "§6.3 hold with reason",
    },
    {
      from: "IN_PROGRESS",
      on: "CompleteExecution",
      to: "EXECUTION_COMPLETE",
      guards: ["prereqStepsComplete", "parallelBranchesJoined"],
      permissions: ["batch.step.perform"],
      cite: "WF-003, WF-005",
    },
    {
      from: "IN_PROGRESS",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void", "batch.release"],
      cite: "WF-020",
    },
    {
      from: "ON_HOLD",
      on: "Resume",
      to: "IN_PROGRESS",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform", "batch.step.void"],
      cite: "§6.3 resume with reason",
    },
    {
      from: "ON_HOLD",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void", "batch.release"],
      cite: "WF-020",
    },
    {
      from: "EXECUTION_COMPLETE",
      on: "SubmitForReview",
      to: "IN_REVIEW",
      permissions: ["batch.step.perform", "batch.step.void"],
      cite: "§6.6 review workflow",
    },
    {
      from: "IN_REVIEW",
      on: "ReturnToProduction",
      to: "IN_PROGRESS",
      guards: ["reasonProvided"],
      permissions: ["exception.review", "batch.release"],
      cite: "§6.6",
    },
    {
      from: "IN_REVIEW",
      on: "Approve",
      to: "QA_APPROVED",
      guards: [
        "noOpenDeviation",
        "reviewComplete",
        "allSignaturesPresent",
        "labResultsApproved",
        "rbePermitted",
      ],
      permissions: ["batch.release"],
      cite: "WF-012, WF-013, WF-028, draft Annex 11 §12.8",
    },
    {
      from: "IN_REVIEW",
      on: "Reject",
      to: "REJECTED",
      guards: ["reasonProvided"],
      permissions: ["batch.release"],
      cite: "§6.6 disposition",
    },
    {
      from: "QA_APPROVED",
      on: "Close",
      to: "CLOSED",
      permissions: ["batch.release", "batch.certify_qp"],
      cite: "Annex 16 §4.1 (QP certification recorded as signature)",
    },
    { from: "REJECTED", on: "Close", to: "CLOSED", permissions: ["batch.release"], cite: "§6.6" },
  ],
};

// ---------------------------------------------------------------- step_execution (§5.3 B)
export type StepState =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "HELD"
  | "SKIPPED"
  | "COMPLETE"
  | "ABORTED"
  | "VOIDED";
export type StepEvent =
  | "Start"
  | "Force"
  | "Hold"
  | "Resume"
  | "Complete"
  | "Skip"
  | "Abort"
  | "Void"
  | "Reexecute";
export const STEP_EXECUTION: Machine<StepState, StepEvent> = {
  id: "step_execution",
  initial: "NOT_STARTED",
  states: ["NOT_STARTED", "IN_PROGRESS", "HELD", "SKIPPED", "COMPLETE", "ABORTED", "VOIDED"],
  events: ["Start", "Force", "Hold", "Resume", "Complete", "Skip", "Abort", "Void", "Reexecute"],
  terminal: ["VOIDED"],
  transitions: [
    {
      from: "NOT_STARTED",
      on: "Start",
      to: "IN_PROGRESS",
      guards: ["prereqStepsComplete", "parallelBranchesJoined", "trainingCurrent", "equipmentReady"],
      permissions: ["batch.step.perform"],
      cite: "WF-003, WF-005, WF-006, WF-015, WF-016",
    },
    {
      // URS-EXE-006 / 21 CFR 11.10(f): the explicit forced-execution path. It lifts the sequencing guards only
      // (a predecessor not complete, a parallel branch not joined) — training and equipment readiness still hold —
      // needs the elevated override permission and a reason, and always raises an exception record (EX-004, EX-005).
      from: "NOT_STARTED",
      on: "Force",
      to: "IN_PROGRESS",
      guards: ["reasonProvided", "trainingCurrent", "equipmentReady"],
      permissions: ["batch.override"],
      cite: "URS-EXE-006, 21 CFR 11.10(f)",
    },
    {
      from: "NOT_STARTED",
      on: "Skip",
      to: "SKIPPED",
      guards: ["stepIsOptional", "reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "WF-007, WF-008",
    },
    {
      from: "IN_PROGRESS",
      on: "Hold",
      to: "HELD",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 Hold",
    },
    {
      from: "IN_PROGRESS",
      on: "Complete",
      to: "COMPLETE",
      guards: ["holdTimeWithinWindow"],
      permissions: ["batch.step.perform"],
      cite: "WF-022",
    },
    {
      from: "IN_PROGRESS",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform", "batch.step.void"],
      cite: "§4.1 Abort",
    },
    {
      from: "HELD",
      on: "Resume",
      to: "IN_PROGRESS",
      permissions: ["batch.step.perform"],
      cite: "§4.1 Restart",
    },
    {
      from: "HELD",
      on: "Abort",
      to: "ABORTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform", "batch.step.void"],
      cite: "§4.1 Abort",
    },
    {
      from: "COMPLETE",
      on: "Void",
      to: "VOIDED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void"],
      cite: "WF-010, PRT-005",
    },
    {
      from: "COMPLETE",
      on: "Reexecute",
      to: "NOT_STARTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void"],
      cite: "WF-009 (new execution record; prior retained)",
    },
    {
      from: "ABORTED",
      on: "Reexecute",
      to: "NOT_STARTED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void"],
      cite: "WF-009",
    },
    {
      from: "SKIPPED",
      on: "Void",
      to: "VOIDED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void"],
      cite: "WF-010",
    },
    {
      from: "ABORTED",
      on: "Void",
      to: "VOIDED",
      guards: ["reasonProvided"],
      permissions: ["batch.step.void"],
      cite: "WF-010",
    },
  ],
};

// ---------------------------------------------------------------- mbr_version (§5.3 A, §5.5, §6.1)
export type MbrState =
  | "DRAFT"
  | "IN_REVIEW"
  | "APPROVED"
  | "EFFECTIVE"
  | "SUPERSEDED"
  | "RETIRED"
  | "OBSOLETE";
export type MbrEvent =
  | "SubmitForReview"
  | "ReturnToDraft"
  | "Approve"
  | "MakeEffective"
  | "Supersede"
  | "Retire"
  | "Obsolete";
export const MBR_VERSION: Machine<MbrState, MbrEvent> = {
  id: "mbr_version",
  initial: "DRAFT",
  states: ["DRAFT", "IN_REVIEW", "APPROVED", "EFFECTIVE", "SUPERSEDED", "RETIRED", "OBSOLETE"],
  events: ["SubmitForReview", "ReturnToDraft", "Approve", "MakeEffective", "Supersede", "Retire", "Obsolete"],
  terminal: ["OBSOLETE"],
  transitions: [
    {
      from: "DRAFT",
      on: "SubmitForReview",
      to: "IN_REVIEW",
      permissions: ["mbr.author"],
      cite: "§6.1 lifecycle",
    },
    {
      from: "IN_REVIEW",
      on: "ReturnToDraft",
      to: "DRAFT",
      guards: ["reasonProvided"],
      permissions: ["mbr.check", "mbr.approve"],
      cite: "§6.1",
    },
    {
      from: "IN_REVIEW",
      on: "Approve",
      to: "APPROVED",
      guards: ["authorNotChecker"],
      permissions: ["mbr.approve"],
      cite: "WF-018, 21 CFR 211.186(a)",
    },
    {
      from: "APPROVED",
      on: "MakeEffective",
      to: "EFFECTIVE",
      permissions: ["mbr.approve"],
      cite: "§6.1 effective dating; WF-017 in-flight batches unaffected",
    },
    {
      from: "APPROVED",
      on: "ReturnToDraft",
      to: "DRAFT",
      guards: ["reasonProvided"],
      permissions: ["mbr.approve"],
      cite: "§6.1",
    },
    {
      from: "EFFECTIVE",
      on: "Supersede",
      to: "SUPERSEDED",
      permissions: ["mbr.approve"],
      cite: "§5.5 template versioning",
    },
    {
      from: "EFFECTIVE",
      on: "Retire",
      to: "RETIRED",
      guards: ["reasonProvided"],
      permissions: ["mbr.approve"],
      cite: "§6.1",
    },
    { from: "SUPERSEDED", on: "Obsolete", to: "OBSOLETE", permissions: ["mbr.approve"], cite: "§6.1" },
    { from: "RETIRED", on: "Obsolete", to: "OBSOLETE", permissions: ["mbr.approve"], cite: "§6.1" },
  ],
};

// ---------------------------------------------------------------- material_lot (§5.3 B, §6.2)
export type LotState =
  | "QUARANTINE"
  | "UNDER_TEST"
  | "RELEASED"
  | "REJECTED"
  | "EXPIRED"
  | "ON_HOLD"
  | "SAMPLED"
  | "RETURNED"
  | "DESTROYED"
  | "CONSUMED";
export type LotEvent =
  | "StartTesting"
  | "Sample"
  | "Release"
  | "Reject"
  | "Expire"
  | "Hold"
  | "ReleaseHold"
  | "Return"
  | "Destroy"
  | "Consume";
export const MATERIAL_LOT: Machine<LotState, LotEvent> = {
  id: "material_lot",
  initial: "QUARANTINE",
  states: [
    "QUARANTINE",
    "UNDER_TEST",
    "RELEASED",
    "REJECTED",
    "EXPIRED",
    "ON_HOLD",
    "SAMPLED",
    "RETURNED",
    "DESTROYED",
    "CONSUMED",
  ],
  events: [
    "StartTesting",
    "Sample",
    "Release",
    "Reject",
    "Expire",
    "Hold",
    "ReleaseHold",
    "Return",
    "Destroy",
    "Consume",
  ],
  terminal: ["DESTROYED", "CONSUMED"],
  transitions: [
    {
      from: "QUARANTINE",
      on: "StartTesting",
      to: "UNDER_TEST",
      permissions: ["masterdata.write"],
      cite: "§6.2 status model",
    },
    {
      from: "QUARANTINE",
      on: "Sample",
      to: "SAMPLED",
      permissions: ["masterdata.write", "batch.step.perform"],
      cite: "§6.2",
    },
    {
      from: "QUARANTINE",
      on: "Reject",
      to: "REJECTED",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write", "batch.release"],
      cite: "§6.2",
    },
    {
      from: "SAMPLED",
      on: "StartTesting",
      to: "UNDER_TEST",
      permissions: ["masterdata.write"],
      cite: "§6.2",
    },
    {
      from: "UNDER_TEST",
      on: "Release",
      to: "RELEASED",
      guards: ["labResultsApproved"],
      permissions: ["batch.release"],
      cite: "§6.2, 21 CFR 211.84",
    },
    {
      from: "UNDER_TEST",
      on: "Reject",
      to: "REJECTED",
      guards: ["reasonProvided"],
      permissions: ["batch.release"],
      cite: "§6.2",
    },
    {
      from: "RELEASED",
      on: "Hold",
      to: "ON_HOLD",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write", "batch.release"],
      cite: "§6.2",
    },
    {
      from: "RELEASED",
      on: "Expire",
      to: "EXPIRED",
      permissions: ["masterdata.write"],
      cite: "§6.2 expiry at point of use (WF-014)",
    },
    {
      from: "RELEASED",
      on: "Consume",
      to: "CONSUMED",
      permissions: ["batch.step.perform", "dispense.perform"],
      cite: "§6.3",
    },
    {
      from: "RELEASED",
      on: "Return",
      to: "RETURNED",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write", "dispense.perform"],
      cite: "§6.3 reconciliation",
    },
    {
      from: "ON_HOLD",
      on: "ReleaseHold",
      to: "RELEASED",
      guards: ["reasonProvided"],
      permissions: ["batch.release"],
      cite: "§6.2",
    },
    {
      from: "ON_HOLD",
      on: "Reject",
      to: "REJECTED",
      guards: ["reasonProvided"],
      permissions: ["batch.release"],
      cite: "§6.2",
    },
    {
      from: "REJECTED",
      on: "Destroy",
      to: "DESTROYED",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write"],
      cite: "§6.2",
    },
    {
      from: "EXPIRED",
      on: "Destroy",
      to: "DESTROYED",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write"],
      cite: "§6.2",
    },
    {
      from: "RETURNED",
      on: "Release",
      to: "RELEASED",
      guards: ["labResultsApproved"],
      permissions: ["batch.release"],
      cite: "§6.2 re-release after return",
    },
    {
      from: "RETURNED",
      on: "Destroy",
      to: "DESTROYED",
      guards: ["reasonProvided"],
      permissions: ["masterdata.write"],
      cite: "§6.2",
    },
  ],
};

// ---------------------------------------------------------------- deviation (§5.3 B, §6.5)
export type DeviationState = "OPEN" | "UNDER_INVESTIGATION" | "CLOSED_ACCEPTED" | "CLOSED_REJECTED";
export type DeviationEvent = "StartInvestigation" | "CloseAccepted" | "CloseRejected";
export const DEVIATION: Machine<DeviationState, DeviationEvent> = {
  id: "deviation",
  initial: "OPEN",
  states: ["OPEN", "UNDER_INVESTIGATION", "CLOSED_ACCEPTED", "CLOSED_REJECTED"],
  events: ["StartInvestigation", "CloseAccepted", "CloseRejected"],
  terminal: ["CLOSED_ACCEPTED", "CLOSED_REJECTED"],
  transitions: [
    {
      from: "OPEN",
      on: "StartInvestigation",
      to: "UNDER_INVESTIGATION",
      permissions: ["deviation.close", "exception.review"],
      cite: "§6.5 investigation",
    },
    {
      from: "UNDER_INVESTIGATION",
      on: "CloseAccepted",
      to: "CLOSED_ACCEPTED",
      guards: ["reasonProvided"],
      permissions: ["deviation.close"],
      cite: "§6.5 closure; 21 CFR 211.192",
    },
    {
      from: "UNDER_INVESTIGATION",
      on: "CloseRejected",
      to: "CLOSED_REJECTED",
      guards: ["reasonProvided"],
      permissions: ["deviation.close"],
      cite: "§6.5",
    },
  ],
};

// ---------------------------------------------------------------- exception_item (§6.5 engineering shape)
export type ExceptionState = "OPEN" | "ACKNOWLEDGED" | "JUSTIFIED" | "ESCALATED_TO_DEVIATION" | "CLOSED";
export type ExceptionEvent = "Acknowledge" | "Justify" | "Escalate" | "Close";
export const EXCEPTION_ITEM: Machine<ExceptionState, ExceptionEvent> = {
  id: "exception_item",
  initial: "OPEN",
  states: ["OPEN", "ACKNOWLEDGED", "JUSTIFIED", "ESCALATED_TO_DEVIATION", "CLOSED"],
  events: ["Acknowledge", "Justify", "Escalate", "Close"],
  terminal: ["CLOSED"],
  transitions: [
    {
      from: "OPEN",
      on: "Acknowledge",
      to: "ACKNOWLEDGED",
      permissions: ["exception.review", "batch.step.void"],
      cite: "§6.5 routing",
    },
    {
      from: "OPEN",
      on: "Escalate",
      to: "ESCALATED_TO_DEVIATION",
      permissions: ["exception.review", "deviation.raise"],
      cite: "§6.5",
    },
    {
      from: "ACKNOWLEDGED",
      on: "Justify",
      to: "JUSTIFIED",
      guards: ["reasonProvided"],
      permissions: ["exception.review"],
      cite: "§6.5 reviewer signs the justification",
    },
    {
      from: "ACKNOWLEDGED",
      on: "Escalate",
      to: "ESCALATED_TO_DEVIATION",
      permissions: ["exception.review"],
      cite: "§6.5",
    },
    { from: "JUSTIFIED", on: "Close", to: "CLOSED", permissions: ["exception.review"], cite: "§6.5" },
    {
      from: "ESCALATED_TO_DEVIATION",
      on: "Close",
      to: "CLOSED",
      guards: ["noOpenDeviation"],
      permissions: ["exception.review"],
      cite: "§6.5 closes with its deviation",
    },
  ],
};

// ---------------------------------------------------------------- phase (ISA-88 / PackML, §4.1)
export type PhaseState =
  | "IDLE"
  | "RUNNING"
  | "HOLDING"
  | "HELD"
  | "RESTARTING"
  | "PAUSING"
  | "PAUSED"
  | "STOPPING"
  | "STOPPED"
  | "ABORTING"
  | "ABORTED"
  | "COMPLETE";
export type PhaseEvent =
  | "Start"
  | "Hold"
  | "Restart"
  | "Pause"
  | "Resume"
  | "Stop"
  | "Abort"
  | "Reset"
  | "TransitionDone"
  | "ProcessComplete";
export const PHASE: Machine<PhaseState, PhaseEvent> = {
  id: "phase",
  initial: "IDLE",
  states: [
    "IDLE",
    "RUNNING",
    "HOLDING",
    "HELD",
    "RESTARTING",
    "PAUSING",
    "PAUSED",
    "STOPPING",
    "STOPPED",
    "ABORTING",
    "ABORTED",
    "COMPLETE",
  ],
  events: [
    "Start",
    "Hold",
    "Restart",
    "Pause",
    "Resume",
    "Stop",
    "Abort",
    "Reset",
    "TransitionDone",
    "ProcessComplete",
  ],
  transitions: [
    {
      from: "IDLE",
      on: "Start",
      to: "RUNNING",
      guards: ["equipmentReady"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 phase model",
    },
    {
      from: "RUNNING",
      on: "Hold",
      to: "HOLDING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 Hold = process-safe pause; reason codes feed deviations",
    },
    { from: "RUNNING", on: "Pause", to: "PAUSING", permissions: ["batch.step.perform"], cite: "§4.1 Pause" },
    {
      from: "RUNNING",
      on: "Stop",
      to: "STOPPING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 Stop",
    },
    {
      from: "RUNNING",
      on: "Abort",
      to: "ABORTING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 Abort",
    },
    {
      from: "RUNNING",
      on: "ProcessComplete",
      to: "COMPLETE",
      guards: ["processConditionMet"],
      permissions: ["batch.step.perform"],
      cite: "§4.1 COMPLETE driven by a process condition, not a timer",
    },
    { from: "HOLDING", on: "TransitionDone", to: "HELD", cite: "§4.1" },
    {
      from: "HOLDING",
      on: "Abort",
      to: "ABORTING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1",
    },
    {
      from: "HELD",
      on: "Restart",
      to: "RESTARTING",
      permissions: ["batch.step.perform"],
      cite: "§4.1 Restart",
    },
    {
      from: "HELD",
      on: "Stop",
      to: "STOPPING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1",
    },
    {
      from: "HELD",
      on: "Abort",
      to: "ABORTING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1",
    },
    { from: "RESTARTING", on: "TransitionDone", to: "RUNNING", cite: "§4.1" },
    { from: "PAUSING", on: "TransitionDone", to: "PAUSED", cite: "§4.1" },
    { from: "PAUSED", on: "Resume", to: "RUNNING", permissions: ["batch.step.perform"], cite: "§4.1 Resume" },
    {
      from: "PAUSED",
      on: "Stop",
      to: "STOPPING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1",
    },
    {
      from: "PAUSED",
      on: "Abort",
      to: "ABORTING",
      guards: ["reasonProvided"],
      permissions: ["batch.step.perform"],
      cite: "§4.1",
    },
    { from: "STOPPING", on: "TransitionDone", to: "STOPPED", cite: "§4.1" },
    { from: "ABORTING", on: "TransitionDone", to: "ABORTED", cite: "§4.1" },
    { from: "STOPPED", on: "Reset", to: "IDLE", permissions: ["batch.step.perform"], cite: "§4.1 Reset" },
    { from: "ABORTED", on: "Reset", to: "IDLE", permissions: ["batch.step.perform"], cite: "§4.1 Reset" },
    { from: "COMPLETE", on: "Reset", to: "IDLE", permissions: ["batch.step.perform"], cite: "§4.1 Reset" },
  ],
};

// ---------------------------------------------------------------- dispense_order (§6.3, URS-EXE-022…028)
export type DispenseState =
  | "PENDING"
  | "IN_PROGRESS"
  | "DISPENSED"
  | "VERIFIED"
  | "ISSUED"
  | "REJECTED"
  | "CANCELLED";
export type DispenseEvent =
  | "Start"
  | "CompleteWeighing"
  | "Verify"
  | "RejectVerification"
  | "Issue"
  | "Cancel"
  | "Reweigh";
/**
 * A dispensing order is weighed by one person and verified by a second before it may be issued to the
 * line: the four-eyes rule of 21 CFR 211.101(c), enforced here as a state the performer cannot leave
 * alone (the `verifierNotPerformer` guard).
 */
export const DISPENSE_ORDER: Machine<DispenseState, DispenseEvent> = {
  id: "dispense_order",
  initial: "PENDING",
  states: ["PENDING", "IN_PROGRESS", "DISPENSED", "VERIFIED", "ISSUED", "REJECTED", "CANCELLED"],
  events: ["Start", "CompleteWeighing", "Verify", "RejectVerification", "Issue", "Cancel", "Reweigh"],
  terminal: ["ISSUED", "CANCELLED"],
  transitions: [
    {
      from: "PENDING",
      on: "Start",
      to: "IN_PROGRESS",
      guards: ["trainingCurrent", "scaleCheckCurrent", "materialReleased"],
      permissions: ["dispense.perform"],
      cite: "URS-EXE-023 daily check interlock, WF-014 released material only",
    },
    {
      from: "PENDING",
      on: "Cancel",
      to: "CANCELLED",
      guards: ["reasonProvided"],
      permissions: ["dispense.perform", "batch.step.void"],
      cite: "§6.3",
    },
    {
      from: "IN_PROGRESS",
      on: "CompleteWeighing",
      to: "DISPENSED",
      guards: ["withinDispenseTolerance", "allContainersLabelled"],
      permissions: ["dispense.perform"],
      cite: "URS-EXE-024, URS-EXE-025",
    },
    {
      from: "IN_PROGRESS",
      on: "Cancel",
      to: "CANCELLED",
      guards: ["reasonProvided"],
      permissions: ["dispense.perform", "batch.step.void"],
      cite: "§6.3",
    },
    {
      from: "DISPENSED",
      on: "Verify",
      to: "VERIFIED",
      guards: ["verifierNotPerformer", "trainingCurrent"],
      permissions: ["dispense.verify"],
      cite: "21 CFR 211.101(c) second-person verification; ESIG-013",
    },
    {
      from: "DISPENSED",
      on: "RejectVerification",
      to: "REJECTED",
      guards: ["verifierNotPerformer", "reasonProvided"],
      permissions: ["dispense.verify"],
      cite: "§6.3",
    },
    {
      from: "REJECTED",
      on: "Reweigh",
      to: "IN_PROGRESS",
      guards: ["reasonProvided", "scaleCheckCurrent"],
      permissions: ["dispense.perform"],
      cite: "§6.3 rework retains the rejected attempt (N9)",
    },
    {
      from: "REJECTED",
      on: "Cancel",
      to: "CANCELLED",
      guards: ["reasonProvided"],
      permissions: ["dispense.perform", "batch.step.void"],
      cite: "§6.3",
    },
    {
      from: "VERIFIED",
      on: "Issue",
      to: "ISSUED",
      guards: ["materialReleased"],
      permissions: ["dispense.perform"],
      cite: "WF-014 status re-checked at point of use, not only at weighing",
    },
    {
      from: "VERIFIED",
      on: "Cancel",
      to: "CANCELLED",
      guards: ["reasonProvided"],
      permissions: ["dispense.perform", "batch.step.void"],
      cite: "§6.3",
    },
  ],
};

export const MACHINES = [
  BATCH,
  STEP_EXECUTION,
  MBR_VERSION,
  MATERIAL_LOT,
  DEVIATION,
  EXCEPTION_ITEM,
  PHASE,
  DISPENSE_ORDER,
  EQUIPMENT_STATUS,
] as const;
